import { CreateUIAnalyticsEvent } from "@atlaskit/analytics-next";

import { ProductKey } from "@/components/signup/utils/products";

import { getPrimaryProduct } from "../hooks/utils/use-cloud-status-utils";
import { getProductQueryParamTeamType } from "../models/user-segmentation";

type createRedirectUrlArgs = {
    isProduction: boolean;
    onDemandUrl: string | undefined;
    products: readonly string[];
    dataRegion: string | undefined;
    cloudId: string;
    admin: boolean | undefined;
    createAnalyticsEvent: CreateUIAnalyticsEvent;
    templateChoice: string;
    templateChoiceId?: string;
    templateId?: string;
    persona?: string;
    jtbd?: string;
    siteRenameRequestId?: string;
    signupSource?: string;
    isFoundingAdmin?: string;
    jtbdTemplateOverride?: string;
    selectedTeamType?: string;
    orgId?: string;
    primaryCollectionProduct?: string;
    projectKey?: string;
};

export const createProductUrl = ({
    isProduction,
    onDemandUrl,
    products,
    dataRegion,
    cloudId,
    admin,
    createAnalyticsEvent,
    templateChoice,
    templateChoiceId,
    persona,
    jtbd,
    siteRenameRequestId,
    signupSource,
    isFoundingAdmin,
    jtbdTemplateOverride,
    selectedTeamType,
    orgId,
    primaryCollectionProduct,
    projectKey
}: // eslint-disable-next-line complexity
createRedirectUrlArgs) => {
    const url: URL = new URL(
        createBaseUrl(
            isProduction,
            onDemandUrl,
            products,
            dataRegion,
            cloudId,
            admin,
            createAnalyticsEvent,
            orgId,
            primaryCollectionProduct,
            projectKey
        )
    );

    const isProductJsw =
        products.length === 1 && products.includes(ProductKey.JIRA_SOFTWARE);

    // This causes browser to retry for 30 seconds if site isn't ready
    url.searchParams.set("src", "signup");

    if (getPrimaryProduct(products) === "rovo" && orgId) {
        return url.toString();
    }

    const hasTemplateChoice = ["scrum", "kanban", "business"].includes(
        templateChoice
    );

    if (hasTemplateChoice) {
        url.searchParams.set("template_choice", templateChoice);
    } else if (templateChoiceId) {
        //only set templateID if not scrum, kanban or business
        url.searchParams.set("templateId", templateChoiceId);
    }

    if (jtbdTemplateOverride) {
        if (
            jtbdTemplateOverride === "scrum" ||
            jtbdTemplateOverride === "kanban"
        ) {
            url.searchParams.set("template_choice", jtbdTemplateOverride);
        } else {
            url.searchParams.set("templateId", jtbdTemplateOverride);
        }
    }

    if (selectedTeamType) {
        url.searchParams.set(
            "tt",
            getProductQueryParamTeamType(selectedTeamType)
        );
    }

    // Get persona data from state.
    if (persona) {
        url.searchParams.set("persona", persona);
    }

    if (isProductJsw) {
        url.pathname = url.pathname + "jira/land";
    }

    if (jtbd) {
        url.searchParams.set("jtbd", jtbd);
    }

    if (siteRenameRequestId) {
        url.searchParams.set("rename", "true");
    }
    if (signupSource === "bbbundle" && isFoundingAdmin === "true") {
        url.searchParams.set("signupSource", signupSource);
        url.searchParams.set("isFoundingAdmin", isFoundingAdmin);
    }

    return url.toString();
};

export const createBaseUrl = (
    isProduction: boolean,
    onDemandUrl: string | undefined,
    products: readonly string[],
    dataRegion: string | undefined,
    cloudId: string,
    admin: boolean | undefined,
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    orgId?: string,
    primaryCollectionProduct?: string,
    projectKey?: string
): string => {
    if (!isValidOnDemandUrl(onDemandUrl || "")) {
        createAnalyticsEvent({
            actionSubject: "invalidUrlOnRedirect",
            eventComponent: "confirmationPage",
            event: "event",
            eventType: "ui"
        }).fire("signup");
        window.history.back();
    }
    const instanceUrl =
        "https://" +
        encodeURIComponent(String(onDemandUrl)) +
        getTopLevelDomain(isProduction);

    const primaryProduct: string | undefined = primaryCollectionProduct
        ? primaryCollectionProduct
        : getPrimaryProduct(products);

    if (admin && primaryProduct !== ProductKey.JIRA_SOFTWARE) {
        return getAdminhubRedirectUrl(isProduction, cloudId);
    }

    if (typeof primaryProduct !== "string") {
        return instanceUrl;
    }

    if (primaryProduct === "rovo" && orgId) {
        return getAdminhubRovoRedirectUrl(isProduction, orgId);
    }

    if (primaryProduct?.includes("guard") && orgId) {
        return getAdminhubGuardRedirectUrl(isProduction, orgId);
    }

    const hasCMK = products.some((product) =>
        product.includes("customer_managed_keys")
    );
    if (hasCMK && orgId) {
        return getAdminhubCMKRedirectUrl(isProduction, orgId);
    }

    return getProductRedirectUrlFromPrimaryProduct(
        isProduction,
        onDemandUrl,
        primaryProduct,
        dataRegion,
        cloudId,
        instanceUrl,
        projectKey
    );
};

const getProductRedirectUrlFromPrimaryProduct = function (
    isProduction: boolean,
    onDemandUrl: string | undefined,
    primaryProduct: string,
    dataRegion: string | undefined,
    cloudId: string,
    instanceUrl: string,
    projectKey?: string
) {
    switch (primaryProduct) {
        case "opsgenie":
            return getOpsgenieRedirectUrl(
                isProduction,
                onDemandUrl,
                dataRegion
            );
        case "statuspage":
            return getStatuspageRedirectUrl(isProduction, cloudId);
        case "com.atlassian.bitbucket":
            return getBitbucketRedirectUrl(isProduction, onDemandUrl);
        case "confluence.ondemand":
            return getConfluenceJiraRedirectUrl(instanceUrl);
        case "devai.ondemand":
            return getRovoDevAgentsRedirectUrl(isProduction, instanceUrl);
        case ProductKey.JIRA_CUSTOMER_SERVICE:
            return getJiraCustomerServiceRedirectUrl(instanceUrl, projectKey);
        case ProductKey.JIRA_SERVICE_MANAGEMENT:
            return getJiraServiceManagementRedirectUrl(instanceUrl, projectKey);
        case ProductKey.JIRA_PRODUCT_DISCOVERY:
            return getJiraProductDiscoveryRedirectUrl(instanceUrl, projectKey);
        default:
            return instanceUrl;
    }
};

const getConfluenceJiraRedirectUrl = function (instanceUrl: string) {
    return instanceUrl + "/wiki";
};

const getJiraProductDiscoveryRedirectUrl = (
    instanceUrl: string,
    projectKey?: string
) =>
    projectKey
        ? `${instanceUrl}/jira/polaris/projects/${projectKey}/ideas`
        : instanceUrl;

const getJiraServiceManagementRedirectUrl = (
    instanceUrl: string,
    projectKey?: string
) =>
    projectKey
        ? `${instanceUrl}/jira/servicedesk/projects/${projectKey}/getting-started`
        : instanceUrl;

const getJiraCustomerServiceRedirectUrl = (
    instanceUrl: string,
    projectKey?: string
) =>
    projectKey
        ? `${instanceUrl}/jira/servicedesk/projects/${projectKey}/get-started`
        : instanceUrl;

const getRovoDevAgentsRedirectUrl = function (
    isProduction: boolean,
    instanceUrl: string
) {
    return !isProduction
        ? instanceUrl + "/rovodev"
        : instanceUrl + "/dev-agents";
};

const getBitbucketRedirectUrl = function (
    isProduction: boolean,
    onDemandUrl?: string
) {
    const route = onDemandUrl
        ? `/${onDemandUrl}/workspace/overview`
        : "/dashboard/overview";

    if (!isProduction) {
        return `https://integration.bb-inf.net${route}`;
    }

    return `https://bitbucket.org${route}`;
};

const getOpsgenieRedirectUrl = function (
    isProduction: boolean,
    onDemandUrl: string | undefined,
    dataRegion: string | undefined
) {
    if (!isProduction) {
        return `https://${onDemandUrl}.atlassian-app.opsgeni.us`;
    }
    if (dataRegion === "europe") {
        return `https://${onDemandUrl}.atlassian-app.eu.opsgenie.com`;
    }
    return `https://${onDemandUrl}.atlassian-app.opsgenie.com`;
};

const getStatuspageRedirectUrl = function (
    isProduction: boolean,
    cloudId: string
) {
    if (!isProduction) {
        return "https://manage.statuspagestaging.com/cloud/" + cloudId;
    }

    return "https://manage.statuspage.io/cloud/" + cloudId;
};

const getAdminhubRedirectUrl = (isProduction: boolean, cloudId: string) => {
    if (!isProduction) {
        return "https://admin.stg.atlassian.com/s/" + cloudId + "/setup";
    }

    return "https://admin.atlassian.com/s/" + cloudId + "/setup";
};

const getAdminhubRovoRedirectUrl = (isProduction: boolean, orgId: string) => {
    if (!isProduction) {
        return "https://admin.stg.atlassian.com/o/" + orgId + "/rovo";
    }

    return "https://admin.atlassian.com/o/" + orgId + "/rovo";
};

const getAdminhubGuardRedirectUrl = (isProduction: boolean, orgId: string) => {
    if (!isProduction) {
        return (
            "https://admin.stg.atlassian.com/o/" + orgId + "/security-checklist"
        );
    }

    return "https://admin.atlassian.com/o/" + orgId + "/security-checklist";
};

const getAdminhubCMKRedirectUrl = (isProduction: boolean, orgId: string) => {
    if (!isProduction) {
        return "https://admin.stg.atlassian.com/o/" + orgId + "/encryption";
    }

    return "https://admin.atlassian.com/o/" + orgId + "/encryption";
};

export const getTopLevelDomain = (isProduction: boolean): string => {
    if (isProduction) {
        return ".atlassian.net";
    } else {
        return ".jira-dev.com";
    }
};

const isValidOnDemandUrl = (siteName: string) => {
    const isUrl = siteName.includes(".");
    return !isUrl;
};
